import { useSelector } from "react-redux";

import { countTotalRows } from "@/components/spatialAnalysis/catchmentDataSection/catchmentDataset";
import { RootState } from "@/store";
import {
  AreaInputType,
  ECatchmentInputBy,
  EOutputAnalysisType,
  EOutputGridType
} from "@/types/spatial";

export type ValidationAnalysisConfigErrors = {
  datasetMessage?: string;
  areaInputMessage?: string;
  outputAnalysisMessage?: string;
  dataWeightMessage?: string;
  resolutionMessage?: string;
};

export const useValidationAnalysisConfigInput = () => {
  const { datasets } = useSelector((state: RootState) => state.dataset);

  const spatial = useSelector((state: RootState) => state.spatial);

  const {
    adminAreaSettings,
    catchmentSettings,
    polygonSettings,
    spatialConfig
  } = spatial;

  const isGridOutput = spatialConfig.outputType === EOutputAnalysisType.GRID;

  // Check if any dataset is available
  const hasAvailableDataset = Object.values(datasets)?.some(
    (item) => item?.data?.available === true
  );

  const datasetMessage = !hasAvailableDataset
    ? "Dataset not selected. Please add dataset first."
    : null;

  const isDefineAreaFilled = (() => {
    switch (spatialConfig.areaInput?.value) {
      case AreaInputType.ADMIN_AREA:
        return !!adminAreaSettings;
      case AreaInputType.CATCHMENT: {
        const inputBy = catchmentSettings?.catchmentInputBy;
        if (inputBy === ECatchmentInputBy.BY_LOCATION) {
          return (catchmentSettings?.placeCoordinates || []).length > 0;
        } else if (inputBy === ECatchmentInputBy.BY_DATASET) {
          return (
            countTotalRows(catchmentSettings?.datasetConfig?.datasets ?? {}) > 0
          );
        }
        break;
      }
      case AreaInputType.POLYGON:
        return Object.keys(polygonSettings).length > 0;
      default:
        return false;
    }
  })();

  const areaInputMessage =
    !spatialConfig.areaInput || !isDefineAreaFilled
      ? "Area not selected. Please choose an area first."
      : null;

  const outputAnalysisMessage =
    isGridOutput && !spatialConfig.gridType
      ? "Output type not selected. Please choose Geohash or H3."
      : null;

  const isGridTypeGeohash = spatialConfig.gridType === EOutputGridType.GEOHASH;
  const isGridTypeH3 = spatialConfig.gridType === EOutputGridType.H3;

  const isGeohashResolutionMissing =
    isGridTypeGeohash && !spatialConfig.gridResolution;
  const isH3ResolutionMissing = isGridTypeH3 && !spatialConfig.h3Resolution;

  const resolutionMessage =
    isGridOutput && (isGeohashResolutionMissing || isH3ResolutionMissing)
      ? "Resolution not selected. Please choose a resolution first."
      : null;

  return {
    canGenerateAnalysisResults:
      !datasetMessage &&
      !areaInputMessage &&
      !outputAnalysisMessage &&
      !resolutionMessage,
    validationErrors: {
      datasetMessage,
      areaInputMessage,
      outputAnalysisMessage,
      resolutionMessage
    },
    hasAvailableDataset
  };
};
